import {useEffectExceptFirstRender} from '@wandb/weave/common/util/hooks';
import {makePropsAreEqual} from '@wandb/weave/common/util/shouldUpdate';
import clsx from 'clsx';
import _ from 'lodash';
import React, {
  memo,
  useCallback,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
// eslint-disable-next-line wandb/no-deprecated-imports
import {Button} from 'semantic-ui-react';

import {useInteractStateWhenOnScreen} from '../../../state/interactState/hooks';
import type * as FilterTypes from '../../../util/filterTypes';
import {type Grouping} from '../../../util/queryTypes';
import * as Run from '../../../util/runs';
import {keyToCss} from '../../../util/runs';
import type * as RunTypes from '../../../util/runTypes';
import {
  type Key as RunKey,
  type Value as RunValue,
} from '../../../util/runTypes';
import {makeRowAddressComponent} from '../../../util/tablecols';
import {type TruncationType} from '../../common/TruncateText/TruncateTextTypes';
import {useRunSelectorRuns} from '../hooks/useRunSelectorRuns';
import type {RunSelectorRunsPartialVars} from '../types';
import {
  type RowForWBTableRow,
  type WBTableColumn,
  type WBTableHoverCellCoords,
} from './types';
import {WBTableCell} from './WBTableCell';

function isUserHoveringRow(
  row: RowForWBTableRow,
  hoverCellCoords: WBTableHoverCellCoords
) {
  const [hoverRowCoord] = hoverCellCoords || [];
  return hoverRowCoord === row.__address__;
}

function isUserHoveringColumn(
  column: WBTableColumn,
  hoverCellCoords: WBTableHoverCellCoords
) {
  const [, hoverColCoord] = hoverCellCoords || [];
  const columnKey = column.key;
  const cssKey = keyToCss(columnKey);
  return cssKey === hoverColCoord;
}

interface WBTableRowProps {
  columns: WBTableColumn[];
  row: RowForWBTableRow;
  pinned?: boolean;
  recursionDepth: number;
  expandedRowAddresses: string[]; // list of expanded rows, an array of rowAddresses
  childrenPaginationMap: {
    [key: string]: number | undefined;
  };
  hoverCellCoords: WBTableHoverCellCoords;
  expanded?: boolean;
  /**
   * When grouping is applied, the user may expand a grouped row to
   * see child rows within that group. Expanding the group triggers a
   * child RunSelectorRuns query to fetch the inner rows.
   *
   * Sometimes a single row is rendered by multiple WBTableRow instances
   * (e.g. once for pinned cols and once for unpinned cols). To avoid
   * making duplicate requests, we expose this prop to allow the
   * parent component to specify if the child query should be skipped.
   */
  shouldSkipChildQuery?: boolean;
  isSingleMode?: boolean;
  showArtifactCounts?: boolean;
  showLogCounts?: boolean;
  topLevelQueryVariables: RunSelectorRunsPartialVars;
  style?: React.CSSProperties;
  truncationType: TruncationType;
  setHoverCellCoords(newCoords: WBTableHoverCellCoords): void;
  setChildrenPagination(rowAddress: string, activePage?: number): void;
  toggleExpandedRow(rowAddress: string): void; // expand or collapse this row
  addFilter(key: RunKey, op: FilterTypes.ValueOp, value: RunValue): void;
}

const WBTableRowComp: React.FC<WBTableRowProps> = ({
  columns,
  row,
  recursionDepth,
  expandedRowAddresses,
  childrenPaginationMap,
  setChildrenPagination,
  hoverCellCoords,
  setHoverCellCoords,
  isSingleMode,
  shouldSkipChildQuery,
  showArtifactCounts,
  showLogCounts,
  pinned,
  expanded, // whether or not the *table* is expanded (not this row - use expandedRowAddresses for that)
  topLevelQueryVariables,

  addFilter,
  toggleExpandedRow,
  truncationType,
}) => {
  const [changeAnimating, setChangeAnimating] = useState<boolean>(false);
  const ref = useRef<HTMLDivElement | null>(null);
  const nextRecursionDepthRef = useRef<number>(recursionDepth + 1);

  useLayoutEffect(() => {
    if (ref.current != null) {
      ref.current.addEventListener('animationend', () => {
        setChangeAnimating(false);
      });
    }
    // If this run was started recently, highlight it
    if (Date.now() - new Date(row.createdAt).getTime() < 30000) {
      setChangeAnimating(true);
    }
    // eslint-disable-next-line
  }, []);

  // comment out to disable row highlighting
  const rowHeartbeatAt: string = row.heartbeatAt;
  useEffectExceptFirstRender(
    () => {
      setChangeAnimating(true);
    },
    [rowHeartbeatAt],
    true
  );

  const rowAddress: string = row.__address__;
  const isGroupExpanded: boolean = _.includes(expandedRowAddresses, rowAddress);

  // If a row is a group, it has children.

  /* Children Pagination */
  const childrenPageSize: number = 10;
  const childrenActivePage: number = childrenPaginationMap[rowAddress] || 1;
  const childrenCount: number = row.groupCounts?.[0] || 0;
  const childrenTotalPages: number = Math.ceil(
    childrenCount / childrenPageSize
  );
  const childrenSliceStart: number =
    (childrenActivePage - 1) * childrenPageSize;
  const childrenSliceEnd: number = childrenSliceStart + childrenPageSize;

  // Transforms the top-level queryVariables into queryVariables for this row's children
  const childrenQuery = useMemo(() => {
    const allGroupKeys: Grouping =
      _.clone(topLevelQueryVariables.grouping) || [];
    let childrenQueryVariables = _.clone(topLevelQueryVariables);
    _.times(nextRecursionDepthRef.current, () => {
      // convert the first group into a filter. (if no groupKey, fetch an individual run.)
      const childrenFilter: RunTypes.Key = allGroupKeys.shift() || {
        section: 'run',
        name: 'name',
      };
      childrenQueryVariables = {
        ...childrenQueryVariables,
        filters: {
          op: 'AND',
          filters: [childrenQueryVariables.filters].concat([
            {
              key: childrenFilter,
              op: '=',
              value: Run.getValue(row, childrenFilter),
            } as FilterTypes.Filter<RunTypes.Key>,
          ]),
        },
        // the children grouping is the parent grouping minus the first grouping key (which is now a filter)
        grouping: allGroupKeys,
      };
    });
    return childrenQueryVariables;
  }, [row, topLevelQueryVariables]);

  const {isLoadingRuns: loadingChildren, runs: childRuns} = useRunSelectorRuns({
    isRootQuery: false,
    skip: !isGroupExpanded || shouldSkipChildQuery,
    queryId: rowAddress,
    pageSize: childrenPageSize * (childrenActivePage + 2),
    partialVariables: childrenQuery,
  });

  const childRows = useMemo(() => {
    return childRuns.map(childRun => {
      return {
        ...childRun,
        runsetInfo: row.runsetInfo,
        selectedRunName: row.selectedRunName,
        __address__: `${rowAddress}-${makeRowAddressComponent(childRun.name)}`,
      };
    });
  }, [childRuns, row.runsetInfo, row.selectedRunName, rowAddress]);

  const isGroup: boolean = row.groupCounts != null || childRows.length > 0;

  const uniqueId: string | null =
    topLevelQueryVariables.grouping != null
      ? Run.uniqueId(row, topLevelQueryVariables.grouping)
      : null;
  const [domRef, isRowHighlighted] = useInteractStateWhenOnScreen(
    interactState =>
      uniqueId != null && interactState.highlight['run:name'] === uniqueId
  );

  const combinedRef = useCallback(
    (el: HTMLDivElement) => {
      ref.current = el;
      domRef(el);
    },
    [domRef]
  );

  const cls = clsx('wb-tree-cell-row-background', {
    'wb-tree-cell--hovering': isUserHoveringRow(row, hoverCellCoords),
    'highlight-change-animation': changeAnimating,
  });

  return (
    <>
      <div ref={combinedRef} className={cls} />
      <div className="wb-tree-padding" />
      {columns.map((column, columnIndex) => {
        const columnKey: RunTypes.Key = column.key;
        const cssKey = keyToCss(columnKey);
        const cls = clsx(
          'wb-tree-cell',
          `wb-tree-cell--column-${cssKey}`,
          `wb-tree-cell--column-${columnIndex}`,
          `wb-tree-cell--row-${rowAddress}`,
          {
            'wb-tree-cell--active':
              row?.selectedRunName === row.name || isRowHighlighted,
            'wb-tree-cell--intersection':
              isUserHoveringColumn(column, hoverCellCoords) &&
              isUserHoveringRow(row, hoverCellCoords),
          }
        );

        return (
          <WBTableCell
            addFilter={addFilter}
            className={cls}
            column={column}
            expanded={expanded}
            isGroup={isGroup}
            isGroupExpanded={isGroupExpanded}
            isHoveringRow={isUserHoveringRow(row, hoverCellCoords)}
            isHoveringColumn={isUserHoveringColumn(column, hoverCellCoords)}
            isSingleMode={isSingleMode}
            key={`${rowAddress}-${column.accessor}`}
            loadingChildren={loadingChildren}
            recursionDepth={recursionDepth}
            row={row}
            showArtifactCounts={showArtifactCounts}
            showLogCounts={showLogCounts}
            toggleExpandedRow={toggleExpandedRow}
            cellHoverProps={{
              onMouseEnter: () => {
                setHoverCellCoords([rowAddress, cssKey]);
              },
              onMouseLeave: () => {
                setHoverCellCoords([]);
              },
            }}
            truncationType={truncationType}
          />
        );
      })}
      <div className="wb-tree-padding" />
      {isGroup && isGroupExpanded && (
        /* GROUPS */
        <React.Fragment>
          {childRows
            .slice(childrenSliceStart, childrenSliceEnd)
            .map(childRow => {
              return (
                <WBTableRowComp
                  addFilter={addFilter}
                  childrenPaginationMap={childrenPaginationMap}
                  columns={columns}
                  expanded={expanded}
                  expandedRowAddresses={expandedRowAddresses}
                  hoverCellCoords={hoverCellCoords}
                  isSingleMode={isSingleMode}
                  key={childRow.__address__}
                  pinned={pinned}
                  recursionDepth={nextRecursionDepthRef.current}
                  row={childRow}
                  setChildrenPagination={setChildrenPagination}
                  setHoverCellCoords={setHoverCellCoords}
                  shouldSkipChildQuery={shouldSkipChildQuery}
                  showArtifactCounts={showArtifactCounts}
                  showLogCounts={showLogCounts}
                  toggleExpandedRow={toggleExpandedRow}
                  topLevelQueryVariables={topLevelQueryVariables}
                  truncationType={truncationType}
                />
              );
            })}

          {!shouldSkipChildQuery && childrenTotalPages > 1 && (
            <div
              className="wb-tree-cell wb-tree-pagination"
              style={{
                gridColumn: `2 / -1`,
                paddingLeft:
                  37 + (expanded ? 18 : 0) + nextRecursionDepthRef.current * 18,
              }}>
              <Button.Group>
                <Button
                  size="tiny"
                  icon="chevron left"
                  disabled={childrenActivePage < 2}
                  onClick={() => {
                    setChildrenPagination(rowAddress, childrenActivePage - 1);
                  }}
                />
                <Button>
                  {childrenSliceStart === 0 ? '01' : childrenSliceStart + 1}-
                  {Math.min(childrenCount, childrenSliceEnd)} of {childrenCount}
                </Button>
                <Button
                  size="tiny"
                  icon="chevron right"
                  disabled={childrenActivePage >= childrenTotalPages}
                  onClick={() => {
                    setChildrenPagination(rowAddress, childrenActivePage + 1);
                  }}
                />
              </Button.Group>
            </div>
          )}
        </React.Fragment>
      )}
    </>
  );
};

const propsAreEqual = makePropsAreEqual({
  name: 'WBTableRow',
  ignore: ['hoverCellCoords'],
  deep: ['columns', 'row', 'topLevelQueryVariables', 'expandedRowAddresses'],
  ignoreFunctions: true,
  debug: false,
  verbose: true,
});

export const WBTableRow = memo(WBTableRowComp, (prevProps, nextProps) => {
  // IF: the user started or stopped hovering this row, we need to update
  const prevIsHoveringRow = isUserHoveringRow(
    prevProps.row,
    prevProps.hoverCellCoords
  );
  const nextIsHoveringRow = isUserHoveringRow(
    nextProps.row,
    nextProps.hoverCellCoords
  );
  if (prevIsHoveringRow !== nextIsHoveringRow) {
    return false;
  }

  // IF: The user has changed what column they are hovering, we need to update
  const [, prevHoverColCoord] = prevProps.hoverCellCoords;
  const [, nextHoverColCoord] = nextProps.hoverCellCoords;
  if (prevHoverColCoord !== nextHoverColCoord) {
    return false;
  }

  // BUT: Aside from those two cases, we should be able to ignore
  //      hoverCellCoords. So we do.
  return propsAreEqual(prevProps, nextProps);
});
